// @greenlight-spec: 28-it-dashboard-ui.md
// @greenlight-spec: 19-design-system.md
// The app-detail header's power control: a single destructive-tinted Power
// button that opens the lifecycle menu (docs/28 §App detail Header). Pause and
// Restore are mutually exclusive — only the one the current status allows is
// offered (you never see "Restore" on a running app) — and Delete is the
// irreversible danger action gated behind a typed-slug confirm. Pause/restore
// is a status transition on the typed `apps.update` op; delete is `apps.delete`.
import * as React from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { CircleCheck, Pause, Play, Power, Trash2 } from 'lucide-react';
import { toast } from 'sonner';
import { isSuspendableAppStatus } from '@greenlight/shared';

import { Button } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { Input } from '@/components/ui/input';
import { GLDrawer } from '@/components/greenlight/GLDrawer';
import { PauseAppDialog } from './PauseAppDialog';
import { AD_BTN_DESTRUCTIVE_TINT, AD_MENUITEM_DESTRUCTIVE } from '@/components/greenlight/surfaces';
import { api } from '@/lib/api';
import { mutationErrorMessage } from '@/lib/apiErrors';
import { useAppActivityActions } from './appActivityActions';
import { refreshAfterLifecycleChange } from './lifecycleQueries';

export interface AppLifecycleMenuProps {
  appId: string;
  appName: string;
  slug: string;
  status: string;
}

// A paused app can be restored; a running one can be paused (the shared
// list decides which statuses count). A deleted app has no lifecycle left.
const SUSPENDED = 'suspended';
const DELETED = 'deleted';
const QUEUED_FOR_DELETION = 'queued_for_deletion';

export function AppLifecycleMenu({ appId, appName, slug, status }: AppLifecycleMenuProps) {
  const queryClient = useQueryClient();
  const updateMutation = api.apps.update.useMutation();
  const deleteMutation = api.apps.delete.useMutation();
  const activity = useAppActivityActions({ appId, appName });
  const [suspendOpen, setSuspendOpen] = React.useState(false);
  const [deleteOpen, setDeleteOpen] = React.useState(false);
  const [deleteSlug, setDeleteSlug] = React.useState('');
  const [pending, setPending] = React.useState<'status' | 'delete' | null>(null);

  const canRestore = status === SUSPENDED;
  // Ask the shared list rather than excluding the statuses that came to mind: the
  // server pauses only from a running state, and offering the action anywhere
  // else sent an owner into a refusal the menu already had the status to predict.
  const canSuspend = isSuspendableAppStatus(status);
  const canDelete = status !== DELETED && status !== QUEUED_FOR_DELETION;
  // A queued deletion that never completes needs its recovery reachable:
  // re-issuing DELETE is idempotent and resumes the remaining teardown steps
  // (docs/08 §Delete flow) — the one state where hiding Delete strands the app.
  const canRetryDeletion = status === QUEUED_FOR_DELETION;
  const isLive = status !== DELETED && status !== QUEUED_FOR_DELETION;
  // Confirming an app is still needed is the owner's own answer, offered on any
  // live app: it is what resets the clock before a notice ever arrives
  // (docs/11 §Activity signals).
  const canConfirmActive = isLive;
  const busy = pending !== null || activity.pending !== null;
  // A deleted app has no lifecycle left — render nothing rather than a
  // dead power button opening an empty menu.
  const hasActions = canRestore || canSuspend || canDelete || canRetryDeletion || canConfirmActive;

  const refreshApp = () => refreshAfterLifecycleChange(queryClient, appId);

  // Restore only — pausing lives in PauseAppDialog, which the banner shares.
  // Restore needs no confirm (it brings an app back), so it fires straight from
  // the menu and guards against a double click itself.
  const restore = async () => {
    if (pending !== null) return;
    setPending('status');
    try {
      const res = await updateMutation.mutateAsync({
        params: { id: appId },
        body: { status: 'active' },
      });
      if (res.status >= 300) throw res;
      toast.success(`${appName} restored.`);
      await refreshApp();
    } catch (err) {
      toast.error(`Failed: ${mutationErrorMessage(err)}`);
    } finally {
      setPending(null);
    }
  };

  const retryDeletion = async () => {
    if (pending !== null) return;
    setPending('delete');
    try {
      const res = await deleteMutation.mutateAsync({ params: { id: appId } });
      if (res.status >= 300) throw res;
      toast.success(`Deletion of ${appName} re-queued — remaining teardown steps will resume.`);
      await refreshApp();
    } catch (err) {
      toast.error(`Failed: ${mutationErrorMessage(err)}`);
    } finally {
      setPending(null);
    }
  };

  const confirmDelete = async () => {
    if (pending !== null) return;
    setPending('delete');
    try {
      const res = await deleteMutation.mutateAsync({ params: { id: appId } });
      if (res.status >= 300) throw res;
      toast.success(`${appName} deleted.`);
      setDeleteOpen(false);
      setDeleteSlug('');
      await refreshApp();
    } catch (err) {
      toast.error(`Failed: ${mutationErrorMessage(err)}`);
    } finally {
      setPending(null);
    }
  };

  if (!hasActions) return null;

  return (
    <>
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          {/* Soft destructive tint: red wash background + red ring, not a solid
              block. Hover darkens (never lightens) to hold contrast per tailwind.md. */}
          <Button
            variant="ghost"
            size="icon"
            aria-label={`Lifecycle actions — ${appName}`}
            className={AD_BTN_DESTRUCTIVE_TINT}
          >
            <Power className="h-4 w-4" />
          </Button>
        </DropdownMenuTrigger>
        {/* Every item is disabled while a lifecycle request is in flight so a
            second action can't be queued behind the first (e.g. Delete
            during a pending restore, or a double Restore). */}
        <DropdownMenuContent align="end" className="w-56">
          {canConfirmActive && (
            <DropdownMenuItem disabled={busy} onSelect={() => void activity.extend()}>
              <CircleCheck className="mr-2 h-4 w-4" aria-hidden />
              {activity.pending === 'extend' ? 'Confirming…' : 'Still needed'}
            </DropdownMenuItem>
          )}
          {canConfirmActive && (canSuspend || canRestore || canRetryDeletion) && (
            <DropdownMenuSeparator />
          )}
          {canSuspend && (
            <DropdownMenuItem disabled={busy} onSelect={() => setSuspendOpen(true)}>
              <Pause className="mr-2 h-4 w-4" aria-hidden /> Pause
            </DropdownMenuItem>
          )}
          {canRestore && (
            <DropdownMenuItem disabled={pending !== null} onSelect={() => void restore()}>
              <Play className="mr-2 h-4 w-4" aria-hidden /> Restore
            </DropdownMenuItem>
          )}
          {canRetryDeletion && (
            <DropdownMenuItem
              disabled={pending !== null}
              onSelect={() => void retryDeletion()}
              data-testid="retry-deletion"
            >
              <Trash2 className="mr-2 h-4 w-4" aria-hidden />
              {pending === 'delete' ? 'Re-queuing…' : 'Retry deletion'}
            </DropdownMenuItem>
          )}
          {canDelete && (
            <>
              <DropdownMenuSeparator />
              <DropdownMenuItem
                disabled={pending !== null}
                className={AD_MENUITEM_DESTRUCTIVE}
                onSelect={() => {
                  setDeleteSlug('');
                  setDeleteOpen(true);
                }}
              >
                <Trash2 className="mr-2 h-4 w-4" aria-hidden /> Delete…
              </DropdownMenuItem>
            </>
          )}
        </DropdownMenuContent>
      </DropdownMenu>

      <PauseAppDialog
        appId={appId}
        appName={appName}
        open={suspendOpen}
        onOpenChange={setSuspendOpen}
        busy={pending !== null}
      />

      {/* Delete confirm — irreversible; re-typing the slug is required on
          every open (never carry a prior confirmation across open/close). */}
      <GLDrawer
        variant="confirm"
        mobileBreakpoint={768}
        open={deleteOpen}
        onOpenChange={(open) => {
          setDeleteOpen(open);
          if (!open) setDeleteSlug('');
        }}
        preventDismiss={pending === 'delete'}
        title={`Delete ${appName}?`}
        description={
          <>
            Permanently removes this app — no rollback at MVP. Type the app slug{' '}
            <span className="font-mono wrap-anywhere">{slug}</span> below to confirm. This is
            audited.
          </>
        }
        footer={
          <>
            <Button
              variant="ghost"
              onClick={() => setDeleteOpen(false)}
              disabled={pending !== null}
            >
              Cancel
            </Button>
            <Button
              variant="destructive"
              disabled={deleteSlug !== slug || pending !== null}
              onClick={() => void confirmDelete()}
            >
              {pending === 'delete' ? 'Deleting…' : 'Delete'}
            </Button>
          </>
        }
      >
        <Input
          value={deleteSlug}
          aria-label="Confirm slug"
          onChange={(e) => setDeleteSlug(e.target.value)}
          placeholder={slug}
        />
      </GLDrawer>
    </>
  );
}
